import { useMemo, useState } from 'react';

import { AlertTriangle, CheckCircle2, DollarSign, Pencil, Plus, Users } from 'lucide-react';
import { toast } from 'sonner';

import {
  AVISO_DE_ACAO_INDISPONIVEL,
  AVISO_DE_CADASTRO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { formatarDinheiro } from '@/nucleo/dinheiro';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { MoneyInput } from '@/ui/MoneyInput';
import { PageHeader } from '@/ui/PageHeader';
import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { Spinner } from '@/ui/Spinner';
import { Alert, AlertDescription, AlertTitle } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/ui/base/card';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { Switch } from '@/ui/base/switch';
import { Tabs, TabsList, TabsTrigger } from '@/ui/base/tabs';
import { Textarea } from '@/ui/base/textarea';

import {
  CONTEXTOS_DE_ALCADA,
  PERFIS_APROVADORES,
  encontrarDefeitos,
  faixaQueDecide,
  normalizarFaixas,
  rotuloDoPerfil,
  useAlcadasDeValor,
  type AlcadaDeValor,
  type ContextoDeAlcada,
  type FaixaNormalizada,
} from './dados';

/** "Sem teto" quando não há limite superior; o valor formatado quando há. */
function tetoFormatado(valor: number | string | null): string {
  return valor == null ? 'Sem teto' : formatarDinheiro(valor);
}

/**
 * `/admin/parametros/workflows/alcadas` — quem aprova o quê, por faixa de valor
 * e por assunto.
 *
 * Irmã de `/admin/parametros/workflows/alcadas-genericas` (o motor de alçadas):
 * lá ficam as regras genéricas que o motor avalia, aqui as faixas de dinheiro.
 *
 * A RÉGUA E O SIMULADOR FUNCIONAM; "SALVAR" NÃO. Encontrar lacuna e
 * sobreposição entre faixas é comparação de números feita no navegador, sem API
 * — e é o que dá valor à tela: uma lacuna é dinheiro que ninguém tem alçada para
 * aprovar, e o defeito só aparece quando alguém desenha as faixas lado a lado.
 * Já criar ou editar faixa precisaria de `/alcadas/valor`, que não existe na API:
 * a tabela `alcadas_aprovacao_valor` (migração `0002_dominio_base.sql`) não tem
 * política de escrita. Os botões mostram o aviso honesto de indisponibilidade
 * (`modulos/operacao/comum/cadastroIndisponivel.ts`).
 *
 * NÃO EXISTE BOTÃO DE EXCLUIR. O protótipo trazia um por linha; ficou de fora
 * pela regra 7 das regras de interface — apagar faixa de alçada é ação de
 * consequência, e oferecer o botão sem poder cumpri-lo é o pior caso da regra.
 */
export function AlcadasValorPage() {
  const [contexto, setContexto] = useState<ContextoDeAlcada>('MULTA');
  const [novaAberta, setNovaAberta] = useState(false);
  const [emEdicao, setEmEdicao] = useState<AlcadaDeValor | null>(null);

  const { data: alcadas, isLoading, isError, error, refetch } = useAlcadasDeValor(contexto);

  const faixas = useMemo(() => normalizarFaixas(alcadas ?? []), [alcadas]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Alçadas por valor"
        description="Define quem aprova cada faixa de dinheiro, por assunto, e quando a decisão exige duas assinaturas."
      >
        <Button onClick={() => setNovaAberta(true)}>
          <Plus className="h-4 w-4 sm:mr-2" aria-hidden="true" />
          <span className="hidden sm:inline">Nova faixa</span>
        </Button>
      </PageHeader>

      <Tabs value={contexto} onValueChange={(valor) => setContexto(valor as ContextoDeAlcada)}>
        <TabsList className="flex-wrap">
          {CONTEXTOS_DE_ALCADA.map((item) => (
            <TabsTrigger key={item.valor} value={item.valor}>
              {item.rotulo}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {isError ? (
        <ErrorState
          variant="load-failed"
          description={
            error?.aviso ??
            'Não foi possível carregar as alçadas agora. Verifique sua conexão e tente novamente.'
          }
          primaryAction={{ label: 'Tentar de novo', onClick: () => void refetch() }}
        />
      ) : isLoading ? (
        <Spinner />
      ) : faixas.length === 0 ? (
        <EmptyState
          icon={DollarSign}
          title="Nenhuma faixa cadastrada neste assunto"
          description="Sem faixa de alçada, nenhum valor deste assunto tem aprovador definido."
        />
      ) : (
        <>
          <ReguaDeFaixas faixas={faixas} />

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Faixas cadastradas</CardTitle>
              <CardDescription>
                Avaliadas de baixo para cima: vale a primeira faixa ativa em que o valor couber.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {faixas.map(({ alcada }) => (
                <div
                  key={alcada.id}
                  className="flex flex-col justify-between gap-3 rounded-lg border bg-card p-3 sm:flex-row sm:items-center"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium">{alcada.nome}</span>
                      {!alcada.ativo && (
                        <Badge variant="outline" className="text-xs">
                          Inativa
                        </Badge>
                      )}
                      {alcada.exige_dupla_aprovacao && (
                        <Badge variant="secondary" className="gap-1 text-xs">
                          <Users className="h-3 w-3" aria-hidden="true" />
                          Dupla alçada
                        </Badge>
                      )}
                    </div>
                    <p className="mt-0.5 font-mono text-xs text-muted-foreground">
                      {formatarDinheiro(alcada.valor_minimo)} até{' '}
                      {tetoFormatado(alcada.valor_maximo)}
                    </p>
                    {alcada.descricao && (
                      <p className="mt-1 text-xs text-muted-foreground">{alcada.descricao}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="text-sm font-semibold">
                        {rotuloDoPerfil(alcada.perfil_aprovador)}
                      </div>
                      {alcada.exige_dupla_aprovacao && (
                        <div className="text-xs text-muted-foreground">
                          com {rotuloDoPerfil(alcada.perfil_segundo_aprovador)}
                        </div>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEmEdicao(alcada)}
                      aria-label={`Editar ${alcada.nome}`}
                    >
                      <Pencil className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </>
      )}

      {novaAberta && <EditorDeFaixa contexto={contexto} onFechar={() => setNovaAberta(false)} />}
      {emEdicao && (
        <EditorDeFaixa contexto={contexto} alcada={emEdicao} onFechar={() => setEmEdicao(null)} />
      )}
    </div>
  );
}

interface ReguaDeFaixasProps {
  faixas: FaixaNormalizada[];
}

/**
 * Mostra as faixas lado a lado, denuncia lacuna e sobreposição, e simula quem
 * aprovaria um valor.
 *
 * Tudo aqui é comparação de números no navegador — funciona de verdade, sem API.
 */
function ReguaDeFaixas({ faixas }: ReguaDeFaixasProps) {
  const [valorSimulado, setValorSimulado] = useState('');

  const defeitos = useMemo(() => encontrarDefeitos(faixas), [faixas]);
  const decisao = useMemo(() => {
    const numero = Number(valorSimulado);
    if (!valorSimulado || !Number.isFinite(numero)) {
      return undefined;
    }
    return faixaQueDecide(faixas, numero);
  }, [faixas, valorSimulado]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <DollarSign className="h-4 w-4 text-primary" aria-hidden="true" />
          Régua de faixas
        </CardTitle>
        <CardDescription>
          Confira se as faixas cobrem toda a escala de valores, sem buraco nem disputa.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {defeitos.length === 0 ? (
          <Alert>
            <CheckCircle2 className="h-4 w-4 text-success" aria-hidden="true" />
            <AlertTitle>Faixas contínuas</AlertTitle>
            <AlertDescription className="text-xs">
              As faixas ativas cobrem a escala de valores sem lacuna nem sobreposição.
            </AlertDescription>
          </Alert>
        ) : (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" aria-hidden="true" />
            <AlertTitle>
              {defeitos.length === 1
                ? '1 problema na parametrização'
                : `${defeitos.length} problemas na parametrização`}
            </AlertTitle>
            <AlertDescription className="space-y-1 text-xs">
              {defeitos.map((defeito) => (
                <div key={`${defeito.tipo}-${defeito.de}-${defeito.ate}`}>
                  {defeito.tipo === 'lacuna' ? (
                    <>
                      <strong>Lacuna:</strong> valores entre {formatarDinheiro(defeito.de)} e{' '}
                      {formatarDinheiro(defeito.ate)} não têm aprovador definido.
                    </>
                  ) : (
                    <>
                      <strong>Sobreposição:</strong> valores entre {formatarDinheiro(defeito.de)} e{' '}
                      {formatarDinheiro(defeito.ate)} caem em mais de uma faixa.
                    </>
                  )}
                </div>
              ))}
            </AlertDescription>
          </Alert>
        )}

        <div className="grid gap-2 sm:max-w-xs">
          <Label htmlFor="alcada-simulacao">Simular um valor</Label>
          <MoneyInput
            id="alcada-simulacao"
            value={valorSimulado}
            onChange={setValorSimulado}
            aria-label="Valor para simular a alçada"
          />
        </div>

        {decisao !== undefined && (
          <div
            className="rounded-lg border bg-muted/30 p-3 text-sm"
            role="status"
            aria-live="polite"
          >
            {decisao === null ? (
              <span className="text-destructive">
                Nenhuma faixa ativa cobre {formatarDinheiro(valorSimulado)} — este valor não teria
                aprovador.
              </span>
            ) : (
              <>
                {formatarDinheiro(valorSimulado)} seria aprovado por{' '}
                <strong>{rotuloDoPerfil(decisao.alcada.perfil_aprovador)}</strong>
                {decisao.alcada.exige_dupla_aprovacao && (
                  <>
                    , com segunda assinatura de{' '}
                    <strong>{rotuloDoPerfil(decisao.alcada.perfil_segundo_aprovador)}</strong>
                  </>
                )}
                . Faixa: {decisao.alcada.nome}.
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface EditorDeFaixaProps {
  contexto: ContextoDeAlcada;
  alcada?: AlcadaDeValor;
  onFechar: () => void;
}

function EditorDeFaixa({ contexto, alcada, onFechar }: EditorDeFaixaProps) {
  const editando = alcada !== undefined;

  const [nome, setNome] = useState(alcada?.nome ?? '');
  const [descricao, setDescricao] = useState(alcada?.descricao ?? '');
  const [minimo, setMinimo] = useState(alcada ? String(alcada.valor_minimo) : '');
  const [maximo, setMaximo] = useState(
    alcada?.valor_maximo == null ? '' : String(alcada.valor_maximo),
  );
  const [perfil, setPerfil] = useState(alcada?.perfil_aprovador ?? 'GESTOR_ADMIN');
  const [dupla, setDupla] = useState(alcada?.exige_dupla_aprovacao ?? false);
  const [segundoPerfil, setSegundoPerfil] = useState(alcada?.perfil_segundo_aprovador ?? 'ADMIN');
  const [ativo, setAtivo] = useState(alcada?.ativo ?? true);

  function salvar() {
    if (!nome.trim() || !minimo) {
      toast.error('Campos obrigatórios', { description: 'Nome e valor mínimo são obrigatórios.' });
      return;
    }
    if (maximo && Number(maximo) <= Number(minimo)) {
      toast.error('Faixa inválida', {
        description: 'O valor máximo precisa ser maior que o mínimo.',
      });
      return;
    }
    if (dupla && segundoPerfil === perfil) {
      toast.error('Dupla alçada inválida', {
        description: 'O segundo aprovador precisa ser diferente do primeiro.',
      });
      return;
    }
    // A tabela `alcadas_aprovacao_valor` não aceita escrita hoje — não há rota
    // na API nem política de escrita no banco. O aviso é honesto: nada foi
    // gravado, e a régua continua mostrando as faixas que já estavam lá.
    mostrarAviso(editando ? AVISO_DE_ACAO_INDISPONIVEL : AVISO_DE_CADASTRO_INDISPONIVEL);
    onFechar();
  }

  return (
    <ResponsiveModal
      open
      onOpenChange={(aberto) => !aberto && onFechar()}
      title={editando ? 'Editar faixa de alçada' : 'Nova faixa de alçada'}
      description={CONTEXTOS_DE_ALCADA.find((item) => item.valor === contexto)?.rotulo ?? contexto}
      desktopMaxWidth="max-w-lg"
      footer={
        <>
          <Button variant="outline" onClick={onFechar}>
            Cancelar
          </Button>
          <Button onClick={salvar}>{editando ? 'Salvar' : 'Cadastrar'}</Button>
        </>
      }
    >
      <div className="grid gap-3 py-2">
        <div className="grid gap-2">
          <Label htmlFor="alcada-nome">Nome da faixa</Label>
          <Input
            id="alcada-nome"
            value={nome}
            onChange={(evento) => setNome(evento.target.value)}
            placeholder="Multa de alto valor"
          />
        </div>

        <div className="grid gap-2">
          <Label htmlFor="alcada-descricao">Descrição</Label>
          <Textarea
            id="alcada-descricao"
            rows={2}
            value={descricao}
            onChange={(evento) => setDescricao(evento.target.value)}
            placeholder="Quando esta faixa se aplica."
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor="alcada-minimo">Valor mínimo</Label>
            <MoneyInput id="alcada-minimo" value={minimo} onChange={setMinimo} />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="alcada-maximo">Valor máximo</Label>
            <MoneyInput id="alcada-maximo" value={maximo} onChange={setMaximo} />
            <p className="text-xs text-muted-foreground">Vazio: faixa sem teto.</p>
          </div>
        </div>

        <div className="grid gap-2">
          <Label htmlFor="alcada-perfil">Aprovador</Label>
          <Select value={perfil} onValueChange={setPerfil}>
            <SelectTrigger id="alcada-perfil">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERFIS_APROVADORES.map((item) => (
                <SelectItem key={item.valor} value={item.valor}>
                  {item.rotulo}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center justify-between rounded-lg border bg-muted/30 p-3">
          <Label htmlFor="alcada-dupla" className="cursor-pointer">
            <span className="text-sm font-medium">Exige dupla aprovação</span>
            <span className="mt-0.5 block text-xs font-normal text-muted-foreground">
              Duas pessoas diferentes precisam assinar a decisão.
            </span>
          </Label>
          <Switch id="alcada-dupla" checked={dupla} onCheckedChange={setDupla} />
        </div>

        {dupla && (
          <div className="grid gap-2">
            <Label htmlFor="alcada-segundo-perfil">Segundo aprovador</Label>
            <Select value={segundoPerfil} onValueChange={setSegundoPerfil}>
              <SelectTrigger id="alcada-segundo-perfil">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERFIS_APROVADORES.map((item) => (
                  <SelectItem key={item.valor} value={item.valor}>
                    {item.rotulo}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="flex items-center justify-between rounded-lg border bg-muted/30 p-3">
          <Label htmlFor="alcada-ativa" className="cursor-pointer">
            <span className="text-sm font-medium">Faixa ativa</span>
            <span className="mt-0.5 block text-xs font-normal text-muted-foreground">
              Faixa inativa não entra na régua nem decide aprovação.
            </span>
          </Label>
          <Switch id="alcada-ativa" checked={ativo} onCheckedChange={setAtivo} />
        </div>
      </div>
    </ResponsiveModal>
  );
}

export default AlcadasValorPage;
